import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useAnalyticsInsights } from '@/hooks/queries/useAnalyticsInsights';
import { useI18n } from '@/hooks/useI18n';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { ApiError } from '@/api';
import { buildAnalyticsInsights } from '@/lib/analyticsInsights';
import { cn } from '@/lib/cn';
import { formatInt, formatNumber, formatPct, formatRelative } from '@/lib/format';
import type { AnalyticsInsightsResponse } from '@/types/api';
import { AnalyticsEmpty } from './AnalyticsEmpty';
import { ConcentrationChart } from './ConcentrationChart';
import { InsightComparisons } from './InsightComparisons';
import { InsightEvidence } from './InsightEvidence';

export function AnalyticsInsightsSection({ periodDays }: { periodDays: number }) {
  const { t } = useI18n();
  const insights = useAnalyticsInsights(periodDays);
  return (
    <section
      id="analytics-insights"
      aria-labelledby="analytics-insights-title"
      className="min-w-0 scroll-mt-20 space-y-4"
    >
      <div className="flex items-center justify-between gap-3">
        <h2
          id="analytics-insights-title"
          className="text-[18px] font-semibold tracking-tight text-text-1"
        >
          {t('Comprendre les performances', 'Understand performance')}
        </h2>
        {insights.data && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void insights.refetch()}
            disabled={insights.isFetching}
            aria-label={t('Actualiser les analyses', 'Refresh insights')}
          >
            <RefreshCw
              className={cn('size-4', insights.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
          </Button>
        )}
      </div>
      {insights.isError ? (
        ApiError.from(insights.error).isNotFound() ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-line-1 bg-bg-1 p-5">
            <p className="text-[13px] text-text-3">
              {t(
                'Les analyses avancées ne sont pas encore disponibles sur ce serveur.',
                'Advanced insights are not available on this server yet.',
              )}
            </p>
            <Button size="sm" onClick={() => void insights.refetch()}>
              {t('Réessayer', 'Retry')}
            </Button>
          </div>
        ) : (
          <ErrorInline error={insights.error} onRetry={() => void insights.refetch()} />
        )
      ) : insights.isLoading || !insights.data ? (
        <div
          role="status"
          aria-label={t('Chargement des analyses', 'Loading insights')}
          className="space-y-4"
        >
          <Skeleton className="h-72 w-full rounded-[14px]" />
          <Skeleton className="h-40 w-full rounded-[14px]" />
        </div>
      ) : (
        <InsightsContent key={periodDays} data={insights.data} />
      )}
    </section>
  );
}

function InsightsContent({ data }: { data: AnalyticsInsightsResponse }) {
  const { t, locale } = useI18n();
  const [threshold, setThreshold] = useState(80);
  const model = useMemo(
    () => buildAnalyticsInsights(data.accounts, threshold),
    [data.accounts, threshold],
  );
  const percent = (value: number | null) =>
    value === null
      ? '—'
      : new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(value);
  const date = (value: string) =>
    new Date(value).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  const hasViews = model.totalViews > 0;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-text-3">
        <span>
          <strong className="font-semibold tabular-nums text-text-1">
            {formatNumber(model.totalViews)}
          </strong>{' '}
          {t('vues gagnées', 'views gained')}
        </span>
        <span>
          <strong className="font-semibold tabular-nums text-text-1">
            {formatInt(model.accounts.length)} / {formatInt(data.totalAccounts)}
          </strong>{' '}
          {t('comptes mesurés', 'measured accounts')}
        </span>
        <span>
          {data.periodDays === 0
            ? t('Tout l’historique', 'All time')
            : `${date(data.from)} – ${date(data.to)}`}{' '}
          · UTC
        </span>
      </div>
      {(model.excludedAccounts.length > 0 || data.unmeasuredReels > 0) && (
        <p className="rounded-lg border border-amber/25 bg-amber-soft px-3 py-2 text-[12px] leading-5 text-text-2">
          {t(
            `Couverture partielle : ${formatInt(model.excludedAccounts.length)} comptes et ${formatInt(data.unmeasuredReels)} Reels sans mesures exploitables, exclus des calculs de vues.`,
            `Partial coverage: ${formatInt(model.excludedAccounts.length)} accounts and ${formatInt(data.unmeasuredReels)} Reels without usable measurements, excluded from view calculations.`,
          )}
        </p>
      )}
      {hasViews ? (
        <>
          <div className="grid min-w-0 overflow-hidden rounded-[14px] border border-line-1 bg-bg-1 lg:grid-cols-[minmax(240px,0.8fr)_minmax(0,1.7fr)]">
            <div className="flex flex-col justify-center border-b border-line-1 bg-gradient-to-br from-violet-soft/60 to-bg-1 p-5 lg:border-b-0 lg:border-r lg:p-6">
              <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-text-3">
                {t('Concentration des vues', 'View concentration')}
              </p>
              <h3
                className="mt-3 text-[28px] font-semibold leading-[1.2] tracking-[-0.035em] text-text-1 sm:text-[30px]"
                aria-live="polite"
              >
                {t(
                  `${formatInt(model.selected.length)} ${model.selected.length === 1 ? 'compte génère' : 'comptes génèrent'} `,
                  `${formatInt(model.selected.length)} ${model.selected.length === 1 ? 'account generates' : 'accounts generate'} `,
                )}
                <span className="text-violet-deep">
                  {percent(model.selectedShare)} {t('des vues.', 'of views.')}
                </span>
              </h3>
              <p className="mt-3 text-[12px] leading-5 text-text-3">
                {t(
                  `${percent(model.selectedAccountShare)} des comptes mesurés`,
                  `${percent(model.selectedAccountShare)} of measured accounts`,
                )}{' '}
                · {formatNumber(model.selectedViews)} {t('vues gagnées', 'views gained')}
              </p>
              <div
                className="mt-5 flex flex-wrap items-center gap-1.5"
                aria-label={t('Part des vues à analyser', 'Share of views to analyze')}
              >
                <span className="mr-1 text-[11px] text-text-3">{t('Seuil', 'Threshold')}</span>
                {[50, 80, 90].map((target) => (
                  <button
                    key={target}
                    type="button"
                    aria-pressed={threshold === target}
                    onClick={() => setThreshold(target)}
                    className={cn(
                      'min-h-11 min-w-11 rounded-md border px-2.5 py-1.5 text-[12px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet [@media(pointer:fine)]:min-h-8',
                      threshold === target
                        ? 'border-violet/30 bg-violet-soft text-violet-deep'
                        : 'border-line-1 bg-bg-1 text-text-2 hover:bg-bg-2',
                    )}
                  >
                    {target} %
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] leading-4 text-text-3">
                {t(
                  'Plus petit groupe atteignant le seuil choisi.',
                  'Smallest group reaching the chosen threshold.',
                )}
              </p>
            </div>
            <ConcentrationChart model={model} />
          </div>
          <InsightComparisons model={model} />
        </>
      ) : (
        <AnalyticsEmpty
          title={
            model.accounts.length
              ? t(
                  'Aucune vue gagnée mesurée sur la période',
                  'No measured view gains in this period',
                )
              : t('Pas encore de mesures exploitables', 'No usable measurements yet')
          }
          description={
            model.accounts.length
              ? t(
                  'Les comptes mesurés totalisent zéro vue gagnée. Une concentration ne peut pas être calculée sur ce total.',
                  'Measured accounts total zero views gained. Concentration cannot be calculated from this total.',
                )
              : t(
                  'La répartition apparaîtra dès que des mesures permettront de calculer les gains de vues.',
                  'The distribution will appear once measurements can establish view gains.',
                )
          }
        />
      )}
      {data.accounts.length > 0 && <InsightEvidence key={threshold} model={model} data={data} />}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 text-[11px] text-text-3">
        <details className="max-w-2xl">
          <summary className="min-h-11 cursor-pointer rounded py-3 underline decoration-line-3 underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet">
            {t('Périmètre et méthode', 'Scope and methodology')}
          </summary>
          <p className="pb-3 leading-5">
            {t(
              `Les vues sont les gains observés sur la période, pas les vues cumulées à vie. ${formatInt(data.measuredReels)} Reels mesurés participent aux calculs. Les comptes sans mesures sont exclus, les vrais zéros sont conservés. Les seuils portent sur la liste complète des comptes mesurés ; la part affichée est celle réellement atteinte. Les vues par Reel utilisent le total des gains divisé par tous les Reels mesurés du groupe. Les abonnés sont les dernières valeurs connues, comparées par médiane en excluant les valeurs manquantes.`,
              `Views are observed gains during the period, not lifetime views. ${formatInt(data.measuredReels)} measured Reels contribute to calculations. Accounts without measurements are excluded; measured zeros are retained. Thresholds use the complete measured-account population; displayed shares are the actual shares reached. Views per Reel divide total gains by all measured Reels in each group. Followers use the latest known counts, compared by median with missing values excluded.`,
            )}
          </p>
          {hasViews && (
            <p className="pb-3 leading-5">
              {t(
                `Part exacte des vues de la sélection : ${formatPct(model.selectedShare, { sign: false, digits: 2 })}.`,
                `Selected-account share of views: ${formatPct(model.selectedShare, { sign: false, digits: 2 })}.`,
              )}
            </p>
          )}
        </details>
        <span className="py-3">
          {t('Dernière observation', 'Latest observation')} ·{' '}
          {formatRelative(data.lastObservationAt)}
        </span>
      </div>
    </div>
  );
}
